import React from "react";
import type { PDFDocumentProxy, PDFPageProxy, RenderTask } from "pdfjs-dist/types/src/display/api";
import type { PageViewport } from "pdfjs-dist/types/src/display/display_utils";
import QRCode from "qrcode";
import { getDocument } from "../../utils/pdfjs"; // do not load directly from pdfjs-dist
import { PdfFieldWidget } from "../../utils/pdfFields";
import { QrCodeSpec, TemplateFieldMeta } from "../../models/TemplateMeta";
import { resolveQrPlacement } from "../../utils/qrPlacement";

type TemplatePreviewPaneProps = {
    pdfBytes: ArrayBuffer | null;
    fieldWidgets: PdfFieldWidget[];
    fieldMeta: TemplateFieldMeta[];
    qrCodes: QrCodeSpec[];
    showFieldNames: boolean;
    showQrCodes: boolean;
    selectedQrId: string | null;
    selectedFieldName?: string | null;
    hoveredFieldName?: string | null;
    hoveredQrId?: string | null;
    scale: number;
    onPageCount?: (count: number) => void;
    onQrPlacementChange?: (qrId: string, update: { x: number; y: number; size: number }) => void;
    onSelectQr?: (qrId: string) => void;
    onHoverQr?: (qrId: string | null) => void;
    onPageMetrics?: (pageIndex: number, metrics: { width: number; height: number }) => void;
    allowSelectionHighlight?: boolean;
    onSelectField?: (fieldName: string) => void;
    onHoverField?: (fieldName: string | null) => void;
};

type PageMetrics = {
    widthPx: number;
    heightPx: number;
    heightPt: number;
    widthPt: number;
    viewport: PageViewport;
};

function toViewportBox(viewport: PageViewport, rect: [number, number, number, number]) {
    const [x1, y1, x2, y2] = viewport.convertToViewportRectangle(rect);
    return {
        left: Math.min(x1, x2),
        top: Math.min(y1, y2),
        width: Math.abs(x2 - x1),
        height: Math.abs(y2 - y1),
    };
}

const TemplatePreviewPane: React.FC<TemplatePreviewPaneProps> = ({
    pdfBytes,
    fieldWidgets,
    fieldMeta,
    qrCodes,
    showFieldNames,
    showQrCodes,
    selectedQrId,
    selectedFieldName,
    hoveredFieldName,
    hoveredQrId,
    scale,
    onPageCount,
    onQrPlacementChange,
    onSelectQr,
    onHoverQr,
    onPageMetrics,
    allowSelectionHighlight = true,
    onSelectField,
    onHoverField,
}) => {
    const [pdfDoc, setPdfDoc] = React.useState<PDFDocumentProxy | null>(null);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (!pdfBytes) {
            setPdfDoc(null);
            return;
        }

        let cancelled = false;

        const loadPdf = async () => {
            try {
                // Use a copy so worker transfer does not mutate shared state buffers.
                const loadingTask = getDocument({ data: new Uint8Array(pdfBytes).slice() });
                const doc = await loadingTask.promise;
                if (!cancelled) {
                    setPdfDoc(doc);
                    setError(null);
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : "Failed to load PDF");
                }
            }
        };

        void loadPdf();

        return () => {
            cancelled = true;
        };
    }, [pdfBytes]);

    React.useEffect(() => {
        if (pdfDoc && onPageCount) {
            onPageCount(pdfDoc.numPages);
        }
    }, [pdfDoc, onPageCount]);

    const fieldMetaByName = React.useMemo(() => {
        const map = new Map<string, TemplateFieldMeta>();
        fieldMeta.forEach((item) => map.set(item.name, item));
        return map;
    }, [fieldMeta]);

    if (!pdfBytes) {
        return <div className="template-editor-preview-empty">No PDF loaded.</div>;
    }

    if (error) {
        return <div className="template-editor-preview-error">{error}</div>;
    }

    if (!pdfDoc) {
        return <div className="template-editor-preview-loading">Loading preview…</div>;
    }

    return (
        <div className="template-editor-preview">
            {Array.from({ length: pdfDoc.numPages }, (_, index) => (
                <PdfPage
                    key={index}
                    pdfDoc={pdfDoc}
                    pageIndex={index}
                    scale={scale}
                    fieldWidgets={fieldWidgets}
                    fieldMetaByName={fieldMetaByName}
                    qrCodes={qrCodes}
                    showFieldNames={showFieldNames}
                    showQrCodes={showQrCodes}
                    selectedQrId={selectedQrId}
                    selectedFieldName={selectedFieldName}
                    hoveredFieldName={hoveredFieldName}
                    hoveredQrId={hoveredQrId}
                    onSelectQr={onSelectQr ?? (() => undefined)}
                    onHoverQr={onHoverQr}
                    onQrPlacementChange={onQrPlacementChange}
                    onPageMetrics={onPageMetrics}
                    allowSelectionHighlight={allowSelectionHighlight}
                    onSelectField={onSelectField}
                    onHoverField={onHoverField}
                />
            ))}
        </div>
    );
};

type PdfPageProps = {
    pdfDoc: PDFDocumentProxy;
    pageIndex: number;
    scale: number;
    fieldWidgets: PdfFieldWidget[];
    fieldMetaByName: Map<string, TemplateFieldMeta>;
    qrCodes: QrCodeSpec[];
    showFieldNames: boolean;
    showQrCodes: boolean;
    selectedQrId: string | null;
    selectedFieldName?: string | null;
    hoveredFieldName?: string | null;
    hoveredQrId?: string | null;
    onSelectQr: (qrId: string) => void;
    onHoverQr?: (qrId: string | null) => void;
    onQrPlacementChange?: (qrId: string, update: { x: number; y: number; size: number }) => void;
    onPageMetrics?: (pageIndex: number, metrics: { width: number; height: number }) => void;
    allowSelectionHighlight: boolean;
    onSelectField?: (fieldName: string) => void;
    onHoverField?: (fieldName: string | null) => void;
};

const PdfPage: React.FC<PdfPageProps> = ({
    pdfDoc,
    pageIndex,
    scale,
    fieldWidgets,
    fieldMetaByName,
    qrCodes,
    showFieldNames,
    showQrCodes,
    selectedQrId,
    selectedFieldName,
    hoveredFieldName,
    hoveredQrId,
    onSelectQr,
    onHoverQr,
    onQrPlacementChange,
    onPageMetrics,
    allowSelectionHighlight,
    onSelectField,
    onHoverField,
}) => {
    const canvasRef = React.useRef<HTMLCanvasElement>(null);
    const [metrics, setMetrics] = React.useState<PageMetrics | null>(null);

    React.useEffect(() => {
        let renderTask: RenderTask | null = null;
        let cancelled = false;

        const renderPage = async () => {
            if (renderTask) {
                renderTask.cancel();
            }
            const page: PDFPageProxy = await pdfDoc.getPage(pageIndex + 1);
            const viewport = page.getViewport({ scale });
            const canvas = canvasRef.current;
            if (!canvas) {
                return;
            }

            canvas.width = viewport.width;
            canvas.height = viewport.height;

            if (!cancelled) {
                const viewportPt = page.getViewport({ scale: 1 });
                setMetrics({
                    widthPx: viewport.width,
                    heightPx: viewport.height,
                    heightPt: viewportPt.height,
                    widthPt: viewportPt.width,
                    viewport,
                });
            }

            renderTask = page.render({ canvas, viewport });
            try {
                await renderTask.promise;
            } catch (error) {
                if (!(error instanceof Error && error.name === "RenderingCancelledException")) {
                    throw error;
                }
            }
        };

        void renderPage();

        return () => {
            cancelled = true;
            if (renderTask?.cancel) {
                renderTask.cancel();
            }
        };
    }, [pdfDoc, pageIndex, scale]);

    React.useEffect(() => {
        if (metrics && onPageMetrics) {
            onPageMetrics(pageIndex, { width: metrics.widthPt, height: metrics.heightPt });
        }
    }, [metrics, onPageMetrics, pageIndex]);

    const widgetsForPage = React.useMemo(
        () => fieldWidgets.filter((widget) => widget.pageIndex === pageIndex),
        [fieldWidgets, pageIndex],
    );

    return (
        <div className="template-editor-page">
            <canvas ref={canvasRef} className="template-editor-canvas" />
            {metrics && (
                <div className="template-editor-overlay" style={{ width: metrics.widthPx, height: metrics.heightPx }}>
                    {showFieldNames &&
                        widgetsForPage.map((widget) => {
                            const meta = fieldMetaByName.get(widget.name);
                            const label = meta?.alternativeName || widget.name;
                            const box = toViewportBox(metrics.viewport, [
                                widget.x,
                                widget.y,
                                widget.x + widget.width,
                                widget.y + widget.height,
                            ]);
                            const isSelected = selectedFieldName === widget.name;
                            const isHovered = hoveredFieldName === widget.name;
                            return (
                                <div
                                    key={`${widget.name}-${widget.x}-${widget.y}`}
                                    className={`template-editor-field-label ${
                                        isSelected ? "is-selected" : isHovered ? "is-hovered" : ""
                                    }`}
                                    style={{
                                        left: box.left,
                                        top: box.top,
                                        width: box.width,
                                        height: box.height,
                                    }}
                                    onMouseEnter={() => onHoverField?.(widget.name)}
                                    onMouseLeave={() => onHoverField?.(null)}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onSelectField?.(widget.name);
                                    }}
                                >
                                    {label}
                                </div>
                            );
                        })}
                    {showQrCodes &&
                        qrCodes.map((qr) => {
                            const placement = resolveQrPlacement(qr, metrics.heightPt, metrics.widthPt);
                            if (!placement || placement.pageIndex !== pageIndex) {
                                return null;
                            }
                            const left = placement.x * scale;
                            const top = (metrics.heightPt - placement.y - placement.height) * scale;
                            const sizePx = placement.width * scale;
                            return (
                                <QrOverlay
                                    key={qr.id}
                                    content={qr.content}
                                    left={left}
                                    top={top}
                                    size={sizePx}
                                    color={qr.color}
                                    backgroundColor={qr.backgroundColor}
                                    isSelected={allowSelectionHighlight && selectedQrId === qr.id}
                                    isHovered={allowSelectionHighlight && hoveredQrId === qr.id}
                                    isDraggable={qr.position.type === "coordinates"}
                                    onSelect={() => onSelectQr(qr.id)}
                                    onMouseEnter={() => onHoverQr?.(qr.id)}
                                    onMouseLeave={() => onHoverQr?.(null)}
                                    onMove={(nextLeft, nextTop, nextSize) => {
                                        if (!onQrPlacementChange) return;
                                        const nextSizePt = nextSize / scale;
                                        const origin = qr.position.origin ?? "top-left";
                                        const fromRight = origin === "top-right" || origin === "bottom-right";
                                        const fromTop = origin === "top-left" || origin === "top-right";
                                        const xLeftPt = nextLeft / scale;
                                        const yTopPt = nextTop / scale;

                                        const xPt = fromRight ? metrics.widthPt - xLeftPt - nextSizePt : xLeftPt;
                                        const yPt = fromTop ? yTopPt : metrics.heightPt - yTopPt - nextSizePt;
                                        onQrPlacementChange(qr.id, { x: xPt, y: yPt, size: nextSizePt });
                                    }}
                                />
                            );
                        })}
                </div>
            )}
        </div>
    );
};

type QrOverlayProps = {
    content: string;
    left: number;
    top: number;
    size: number;
    color?: string | null;
    backgroundColor?: string | null;
    isSelected: boolean;
    isHovered: boolean;
    isDraggable: boolean;
    onSelect: () => void;
    onMove: (left: number, top: number, size: number) => void;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
};

const QrOverlay: React.FC<QrOverlayProps> = ({
    content,
    left,
    top,
    size,
    color,
    backgroundColor,
    isSelected,
    isHovered,
    isDraggable,
    onSelect,
    onMove,
    onMouseEnter,
    onMouseLeave,
}) => {
    const [dataUrl, setDataUrl] = React.useState<string | null>(null);
    const dragState = React.useRef<{
        active: boolean;
        startX: number;
        startY: number;
        startLeft: number;
        startTop: number;
        moved: boolean;
    } | null>(null);

    React.useEffect(() => {
        let cancelled = false;

        const createQr = async () => {
            try {
                const url = await QRCode.toDataURL(content || " ", {
                    width: Math.max(40, Math.round(size)),
                    margin: 0,
                    color: {
                        dark: color ?? "#000000",
                        light: backgroundColor ?? "#ffffff",
                    },
                });
                if (!cancelled) {
                    setDataUrl(url);
                }
            } catch {
                if (!cancelled) {
                    setDataUrl(null);
                }
            }
        };

        void createQr();

        return () => {
            cancelled = true;
        };
    }, [content, size, color, backgroundColor]);

    const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!isDraggable) {
            onSelect();
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        onSelect();

        const target = event.currentTarget;
        target.setPointerCapture(event.pointerId);
        dragState.current = {
            active: true,
            startX: event.clientX,
            startY: event.clientY,
            startLeft: left,
            startTop: top,
            moved: false,
        };
    };

    const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!dragState.current?.active) return;

        const parent = event.currentTarget.parentElement;
        if (!parent) return;

        const parentRect = parent.getBoundingClientRect();
        const deltaX = event.clientX - dragState.current.startX;
        const deltaY = event.clientY - dragState.current.startY;
        if (Math.abs(deltaX) + Math.abs(deltaY) > 3) {
            dragState.current.moved = true;
        }
        let nextLeft = dragState.current.startLeft + deltaX;
        let nextTop = dragState.current.startTop + deltaY;

        const maxLeft = Math.max(0, parentRect.width - size);
        const maxTop = Math.max(0, parentRect.height - size);

        nextLeft = Math.min(Math.max(0, nextLeft), maxLeft);
        nextTop = Math.min(Math.max(0, nextTop), maxTop);

        onMove(nextLeft, nextTop, size);
    };

    const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!dragState.current?.active) return;
        dragState.current = null;
        event.currentTarget.releasePointerCapture(event.pointerId);
    };

    const quietZone = Math.max(2, Math.round(size * 0.04));
    const safeBackground = backgroundColor ?? "#ffffff";

    return (
        <div
            className={`template-editor-qr ${isSelected ? "is-selected" : isHovered ? "is-hovered" : ""}`}
            style={{
                left,
                top,
                width: size,
                height: size,
                padding: quietZone,
                backgroundColor: safeBackground,
            }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onClick={(event) => event.stopPropagation()}
        >
            {dataUrl ? <img src={dataUrl} alt="QR" draggable={false} /> : <span>QR</span>}
        </div>
    );
};

export default TemplatePreviewPane;
